// =============================================================================
// Commons
// =============================================================================

export function nullify(str) {
  if (str === undefined || str === null) {
    return null;
  }

  if (typeof str === 'string') {
    str = str.trim();

    if (str === '') {
      return null;
    }
  }

  return str;
}


// =============================================================================
// Text to boolean conversion
// =============================================================================

export function asBoolean(param) {
  if (param === undefined || param === null) {
    return undefined;
  }

  if (param === '1' || param === 'true' || param === 1 || param === true) {
    return true;
  }

  if (param === '0' || param === 'false' || param === 0 || param === false) {
    return false;
  }

  throw Error('Unexpected value for param [' + param + ']: it should be 0/1/true/false');
}

// =============================================================================
// Number to text / Text to number conversion
// =============================================================================

import accounting from 'accounting';

export function initAccounting(userInfo = {}) {
  accounting.settings = {
    currency: {
      symbol: '',     // default currency symbol is '$'
      format: '%s%v', // controls output: %s = symbol, %v = value/number
      precision: 2,   // decimal places
      decimal: userInfo.decimalSeparator || ',',   // decimal point separator
      thousand: userInfo.thousandSeparator || '.'  // thousands separator
    },
    number: {
      precision: 2,   // decimal places
      decimal: userInfo.decimalSeparator || ',',   // decimal point separator
      thousand: userInfo.thousandSeparator || '.'  // thousands separator
    }
  };
}

export function text2number(str) {
  const value = nullify(str);
  if (!value) return null;

  if (value === 'NaN') {
    return NaN;
  }

  const ret = accounting.unformat(str);
  // console.log('text2number(', str, ') = ', ret);
  return ret;
}

export function number2text(value, precision, thousandSeparator, decimalSeparator) {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === 'string') {
    if (value === 'NaN') {
      return 'N/A';
    } else if (value === 'Infinity') {
      return 'Inf';
    }
  }

  if (typeof value !== 'number') {
    throw Error('Error converting value [' + value + ']: it is not a number');
  }

  if (isNaN(value)) {
    return 'N/A';
  }

  const ret = accounting.formatMoney(value, undefined, precision, thousandSeparator, decimalSeparator);
  // console.log('number2text(', value, ') = ', ret);
  return ret;
}

const integerRe = /^[-+]?(\d+)$/;

export function isInteger(str) {
  return integerRe.test(str);
}

const numericRe = /^[-+]?(\d+|\d+\.\d*|\d*\.\d+)$/;

export function isNumeric(str) {
  return numericRe.test(str);
}

// =============================================================================
// Number to file size conversion
// =============================================================================

export function number2fileSize(size, divisor = 1024) {
  if (size === undefined || size === null) {
    return size;
  }

  const precision = 2;
  if (size < divisor) {
    return number2text(size, precision) + ' B';
  }
  size /= divisor;
  if (size < divisor) {
    return number2text(size, precision) + ' KB';
  }
  size /= divisor;
  if (size < divisor) {
    return number2text(size, precision) + ' MB';
  }
  size /= divisor;
  if (size < divisor) {
    return number2text(size, precision) + ' GB';
  }
  size /= divisor;
  return number2text(size, precision) + ' TB';
}

// =============================================================================
// Moment to text / Text to moment conversion
// =============================================================================

import * as moment from 'moment';

const DEFAULT_MOMENT_FORMATS = [
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DDTHH:mm:ss.S',
    'YYYY-MM-DDTHH:mm:ss.SS',
    'YYYY-MM-DDTHH:mm:ss.SSS',
    'YYYY-MM-DD'
];

export function isMoment(value) {
  return moment.isMoment(value);
}

// TODO check for errors
// Strict parsing requires that the format and input match exactly, including delimeters
/**
 * @param param: string
 * @param fmt: string | string[]
 * @param strictParsing. boolean
 * @return moment
 */
export function text2moment(param, fmt = DEFAULT_MOMENT_FORMATS, strictParsing) {
  if (param === undefined || param === null) {
    return param;
  }

  // console.log('text2moment', param);
  if (typeof fmt === 'string') {
    fmt = [fmt];
  }

  for (let i = 0; i < fmt.length; i++) {
    // console.log('\tfmt', fmt[i]);

    const ret = moment(param, fmt[i], strictParsing);
    if (ret.isValid()) {
      // console.log('\tret', ret);
      return ret;
    }
  }

  throw Error(`Unsupported format: ${param} Expected: ${fmt}`);
}

/**
 * @param param: moment
 * @param fmt: string | string[]
 * @return string
 */
export function moment2text(param, fmt = DEFAULT_MOMENT_FORMATS) {
  if (param === undefined || param === null) {
    return param;
  }

  if (Array.isArray(fmt)) {
    fmt = fmt[0];
  }

  const ret = param.format(fmt);
  // console.log('moment2text', param, '->', ret);
  return ret;
}

export function unix2moment(param) {
  if (param === undefined || param === null) {
    return param;
  }

  return moment.unix(param);
}

export function moment2unix(param) {
  if (param === undefined || param === null) {
    return param;
  }

  return param.unix();
}

// =============================================================================
// Json
// =============================================================================

// The object in which the key was found is provided as the replacer's this parameter.
function replacer(k, v) {
  // console.log('Replacing', k, this[k]);
  if (moment.isMoment(this[k])) {
    return moment2text(this[k]);
  }
  return v;
}

export function stringifyJson(obj) {
  return JSON.stringify(obj, replacer);
}

export function parseJson(str) {
  return JSON.parse(str);
}

// =============================================================================
// Unique ID
// =============================================================================

export function uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    let r = Math.random() * 16 | 0;
    let v = c == 'x'
        ? r
        : (r & 0x3 | 0x8)
        ;
    return v.toString(16);
  });
}

// =============================================================================
// Compare files
// =============================================================================

export function isSameFile(a, b) {
  if (a.name !== b.name
      || a.size !== b.size
      || a.type !== b.type
      || a.lastModified !== b.lastModified) {
    return false;
  }

  // TODO check for content

  return true;
}

// =============================================================================
// Arrays
// =============================================================================

export function subarray(arr, start, end) {
  if (!arr) {
    return arr;
  }

  const ret = [];
  if (end >= arr.length) {
    end = arr.length - 1;
  }

  if (start < 0) {
    start = 0;
  }

  for (let i = start; i <= end; i++) {
    ret.push(arr[i]);
  }

  return ret;
}

// =============================================================================
// Error to text conversion
// =============================================================================

export function error2text(data) {
  console.log('Converting error', data, 'to text');
  if (data === undefined || data === null) {
    data = 'No message';
  } else if (typeof data === 'string') {
    try {
      const json = JSON.parse(data);
      data = error2text(json);
    } catch (e) {
      // Do nothing;
    }
  } else if (typeof data === 'object') {
    if (data.message) {
      data = data.message;
    } else if (data.error) {
      data = data.error;
    } else {
      data = 'No message <unconvertible data>';
    }
  } else {
    data = 'No message <unconvertible data>';
  }
  return data;
}

// =============================================================================
// Audio
// =============================================================================

// browsers limit the number of concurrent audio contexts, so you better re-use'em
const audioContext = new AudioContext();

export function beep({ vol, freq, duration } = { vol: 100, freq: 520, duration: 200 }) {
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();
  oscillator.connect(gain);
  oscillator.frequency.value = freq;
  oscillator.type = "square";
  gain.connect(audioContext.destination);
  gain.gain.value = vol * 0.01;
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + duration * 0.001);
}

// =============================================================================
// Files
// =============================================================================

export function cloneFile(file) {
  const bits = [file];
  const name = file.name;
  const options = { type: file.type };
  try {
    // If this call fails, we go for Blob
    return new File(bits, name, options);
  }
  catch (e) {
      // If we reach this point a new File could not be constructed
      var myBlob = new Blob(bits, options || {});
      myBlob.lastModified = new Date();
      myBlob.name = name;
      return myBlob;
  }
}

export function renameFile(file, newName) {
  const bits = [file];
  const options = { type: file.type };
  try {
    // If this call fails, we go for Blob
    return new File(bits, newName, options);
  }
  catch (e) {
      // If we reach this point a new File could not be constructed
      var myBlob = new Blob(bits, options || {});
      myBlob.lastModified = new Date();
      myBlob.name = newName;
      return myBlob;
  }
}
